"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NO_REMARKS = exports.editRemarkSchema = exports.addRemarksSchema = exports.addRemarkSchema = exports.REMARK_BULK_MAX = exports.REMARK_MENTIONS_MAX = exports.REMARK_BODY_MAX = exports.REMARK_VISIBILITY_LABELS = exports.REMARK_VISIBILITIES = exports.REMARK_TAG_TONES = exports.REMARK_TAG_LABELS = exports.REMARK_TAGS = void 0;
exports.remarkToneOf = remarkToneOf;
exports.initialsOf = initialsOf;
exports.remarkLine = remarkLine;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// ADR 0049 (F23.13, DECISIONS-LOG 2026-10-17 "Ranking 2.0 Part 1" Q3–Q4): a remark is a note on an
// application — tags from a fixed list and/or text, seen by the team or by its author only, and on a
// Team remark @mentions that send N-94. Every note on an application is a remark; there is one kind.
/** Fixed for now, as §L30 keeps the importance levels fixed; editable lists come with the post-V1 backlog. */
exports.REMARK_TAGS = ['strong_fit', 'call_first', 'notice_period', 'check_salary', 'cv_gaps', 'overqualified', 'not_a_fit'];
exports.REMARK_TAG_LABELS = {
    strong_fit: 'Strong fit',
    call_first: 'Call first',
    notice_period: 'Notice period',
    check_salary: 'Check salary',
    cv_gaps: 'CV has gaps',
    overqualified: 'Overqualified',
    not_a_fit: 'Not a fit',
};
exports.REMARK_TAG_TONES = {
    strong_fit: 'good',
    call_first: 'neutral',
    notice_period: 'check',
    check_salary: 'check',
    cv_gaps: 'check',
    overqualified: 'neutral',
    not_a_fit: 'no',
};
const TONE_WEIGHT = { no: 3, check: 2, good: 1, neutral: 0 };
/** A remark's one dot: its most urgent tag wins, so "Strong fit · Notice period" reads as something to check. */
function remarkToneOf(tags) {
    return tags.reduce((tone, tag) => (TONE_WEIGHT[exports.REMARK_TAG_TONES[tag]] > TONE_WEIGHT[tone] ? exports.REMARK_TAG_TONES[tag] : tone), 'neutral');
}
exports.REMARK_VISIBILITIES = ['team', 'private'];
exports.REMARK_VISIBILITY_LABELS = {
    team: 'Team',
    private: 'Only me',
};
exports.REMARK_BODY_MAX = 2000;
exports.REMARK_MENTIONS_MAX = 10;
/** One "Add a remark" on selected rows writes one remark on each; RNK-01's selection is capped like §L9's. */
exports.REMARK_BULK_MAX = 500;
const NEEDS_CONTENT = 'Pick a tag or write something.';
const remarkFields = {
    tags: zod_1.z
        .array(zod_1.z.enum(exports.REMARK_TAGS))
        .max(exports.REMARK_TAGS.length)
        .default([])
        .transform((tags) => exports.REMARK_TAGS.filter((tag) => tags.includes(tag))),
    body: zod_1.z.string().trim().max(exports.REMARK_BODY_MAX, `At most ${exports.REMARK_BODY_MAX} characters.`).default(''),
    visibility: zod_1.z.enum(exports.REMARK_VISIBILITIES).default('team'),
    /** Only on a Team remark: an Only me remark is for nobody else, so it mentions nobody. */
    mentionUserIds: zod_1.z.array(schemas_1.uuidSchema).max(exports.REMARK_MENTIONS_MAX, `Mention at most ${exports.REMARK_MENTIONS_MAX} people.`).default([]),
};
function hasContent(remark) {
    return remark.tags.length > 0 || remark.body !== '';
}
exports.addRemarkSchema = zod_1.z.object(remarkFields).refine(hasContent, { message: NEEDS_CONTENT, path: ['body'] });
/** One remark on each selected row; mentions are made one remark at a time, so one send doesn't ping a person 200 times. */
exports.addRemarksSchema = zod_1.z
    .object({ tags: remarkFields.tags, body: remarkFields.body, visibility: remarkFields.visibility, applicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Select someone first.').max(exports.REMARK_BULK_MAX) })
    .refine(hasContent, { message: NEEDS_CONTENT, path: ['body'] });
exports.editRemarkSchema = zod_1.z
    .object({ ...remarkFields, version: zod_1.z.number().int().positive() })
    .refine(hasContent, { message: NEEDS_CONTENT, path: ['body'] });
exports.NO_REMARKS = { count: 0, latest: null, tags: [] };
/** "Nabil Bari" → "NB": the author's initials on a remark chip. */
function initialsOf(name) {
    if (!name)
        return '—';
    const parts = name.trim().split(/\s+/).filter((part) => part !== '');
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return `${first}${last}`.toUpperCase() || '—';
}
/** A remark in one line: its tags, then its text — what the list and a notification show. */
function remarkLine(remark) {
    const tags = remark.tags.map((tag) => exports.REMARK_TAG_LABELS[tag]).join(' · ');
    if (tags && remark.body)
        return `${tags} — ${remark.body}`;
    return tags || remark.body;
}
//# sourceMappingURL=remarks.js.map